/**
 * What shape each picture on a merchant's website should be.
 *
 * <b>Why the builder needs this at all.</b> A restaurant owner uploads what their phone took — 4032 ×
 * 3024, portrait as often as landscape. Every slot on the page renders with `object-fit: cover`, so the
 * browser crops whatever it is given to the slot's own proportions. Left alone that means the merchant
 * chooses a photograph, sees the top of it on the homepage and the middle of it on a phone, and has no
 * way to find out why. Cropping at upload time, to the ratio the slot actually renders at, is the only
 * point where the result is predictable.
 *
 * <b>Cropped, never refused.</b> The ordering banner used to demand exactly 1280 × 400 and reject
 * anything else with its measurements — correct, useless, and a dead end for someone holding one photo
 * of their restaurant. The compressor already re-encodes every upload; making it crop as well costs
 * nothing and turns a refusal into a result.
 *
 * <b>Never upscaled.</b> A 700-px photo stretched to 1600 is blurry AND heavier. It is cropped to the
 * right shape at its own size and the merchant is told it is smaller than ideal — a note, not a bar.
 *
 * The targets are 2× the CSS size each slot renders at, because retina screens are the common case and
 * the WebP re-encode absorbs the weight.
 */

/**
 * `cover` crops to the slot's proportions; `contain` fits inside them, for logos that must not lose an
 * edge. `min` is the floor an upload is REFUSED under — the target itself for photographs, where
 * anything smaller renders soft on a retina screen, and a much lower bar for marks and logos, which
 * are legitimately small files.
 */
const SLOTS = {
    /* ── homepage ── */

    // Full-bleed, min-height: min(76vh, 680px). Wide on a laptop, tall on a phone — the same file has
    // to survive both crops, so it is deliberately squarer than the desktop shape alone would need.
    homeHero:    { width: 1600, height: 1000, fit: 'cover',   label: 'Toppbildet' },

    // A column beside the text: roughly half the 1200-px page at 4:3.
    homeAbout:   { width: 1200, height: 900,  fit: 'cover',   label: 'Om oss-bildet' },

    // Grid tiles are 150–200 px tall and one or two columns wide; the lead tile spans 2 × 2.
    homeGallery: { width: 1200, height: 800,  fit: 'cover',   label: 'Galleribildet' },

    // Award marks and delivery logos, rendered 46 px tall. Contain: a cropped logo is a broken logo,
    // and the transparent background must survive, which WebP keeps.
    homeBadge:   { width: 320,  height: 320,  fit: 'contain', label: 'Merket',
                   min: { width: 120, height: 120 } },

    /* ── ordering page ── */

    // A short banner, not a hero: the ordering page puts its store chrome directly beneath it.
    orderingHero: { width: 1280, height: 400, fit: 'cover',   label: 'Bannerbildet' },

    // Sits in the header bar at a fixed height.
    logo:         { width: 512,  height: 512, fit: 'contain', label: 'Logoen',
                   min: { width: 128, height: 128 } }
};

/** The slot a homepage block's field renders into. */
function forHomeField(blockType, field) {
    if (blockType === 'hero' && field === 'image') return SLOTS.homeHero;
    if (blockType === 'hero') return SLOTS.homeBadge;          // badges[n].image
    if (blockType === 'about') return SLOTS.homeAbout;
    if (blockType === 'gallery') return SLOTS.homeGallery;
    return SLOTS.homeGallery;                                   // a sane shape for anything added later
}

/** The one-line hint under an upload control. */
function hint(slot) {
    return slot.fit === 'contain'
        ? 'Minst ' + slot.width + ' × ' + slot.height + ' piksler. Bildet skaleres ned, aldri beskåret.'
        : 'Minst ' + slot.width + ' × ' + slot.height + ' piksler. Større bilder beskjæres fra midten.';
}

/** Landscape / portrait / square, for a message that names the shape rather than only the numbers. */
function shape(width, height) {
    if (width > height * 1.05) return 'liggende';
    if (height > width * 1.05) return 'stående';
    return 'kvadratisk';
}

/**
 * Judge a picked file before a single byte is uploaded.
 *
 * Two answers, and they are deliberately different in kind:
 *
 * <b>Too small is a refusal.</b> Nothing can recover detail that is not in the file, so uploading it
 * would put a soft picture on the merchant's front page and tell them about it afterwards. They are
 * asked for a bigger one, with both sizes named so they know what «bigger» means.
 *
 * <b>Wrong shape is a question.</b> The image is good enough; it simply does not have the slot's
 * proportions, and the page crops from the centre. That is usually fine and occasionally cuts the
 * wrong half, so the merchant is told how much falls away and decides. Refusing here would be a dead
 * end for someone holding one good photograph of their dining room in portrait.
 *
 * @param plan the geometry from ImageCompressor.plan — passed in so the warning and the crop can
 *             never be computed differently
 * @returns {{verdict: 'ok'|'tooSmall'|'crop', message: string}}
 */
function inspect(size, slot, plan) {
    const was = size.width + ' × ' + size.height + ' piksler';

    if (plan.tooSmall) {
        const min = slot.min || { width: slot.width, height: slot.height };
        return {
            verdict: 'tooSmall',
            message: slot.label + ' du valgte er ' + was + '. Det må være minst '
                + min.width + ' × ' + min.height + ' piksler, ellers blir det uskarpt på store skjermer. '
                + 'Velg et større bilde.'
        };
    }

    // Under a tenth of the picture is a trim, not a crop — saying so every time would train the
    // merchant to click past the dialog that matters.
    if (slot.fit === 'cover' && plan.cropFraction > 0.10) {
        return {
            verdict: 'crop',
            message: 'Bildet er ' + was + ' (' + shape(size.width, size.height) + '), mens '
                + slot.label.toLowerCase() + ' er ' + slot.width + ' × ' + slot.height + '. '
                + 'Omtrent ' + Math.round(plan.cropFraction * 100) + ' % av bildet faller bort når det '
                + 'beskjæres fra midten. Vil du laste det opp likevel?'
        };
    }

    return { verdict: 'ok', message: '' };
}

export default { SLOTS, forHomeField, hint, inspect };
